import React, { ReactNode, useMemo, useState } from 'react';
import {
  Autocomplete,
  CircularProgress,
  FilterOptionsState,
  TextField,
} from '@mui/material';
import debounce from 'lodash/debounce';

interface IProps<T> {
  label: string;
  loading: boolean;
  value: T;
  options: T[];
  helperText?: string;
  error?: boolean;
  searchFunction: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  fetchFunction: (
    e: React.UIEvent<HTMLUListElement, UIEvent>,
    search: string
  ) => void;
  getOptionLabel: (option: T) => string;
  renderOption: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: T
  ) => ReactNode;
  onChange: (event: React.SyntheticEvent<Element, Event>, newValue: T) => void;
  onInputChange: (
    event: React.SyntheticEvent<Element, Event>,
    newInputValue: string
  ) => void;
  disabled?: boolean;
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
  sx?: any;
  key?: string;
}

const AutoComplete = <T extends object>(
  props: IProps<T> & { children?: ReactNode }
) => {
  const {
    label,
    loading,
    value,
    error,
    helperText,
    fetchFunction,
    options,
    searchFunction,
    onChange,
    onInputChange,
    renderOption,
    getOptionLabel,
    disabled,
    filterOptions,
    sx,
    key,
  } = props;
  const [search, setSearch] = useState<string>('');

  const debounceWithSearch = useMemo(() => {
    return debounce(searchFunction, 1500);
  }, [searchFunction]);

  const onChangeInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setSearch(value);
    debounceWithSearch(e);
  };

  return (
    <Autocomplete
      key={key}
      filterOptions={filterOptions}
      value={value}
      onInputChange={onInputChange}
      onChange={onChange}
      id='controllable-states-demo'
      disableClearable
      options={options}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      renderInput={(params) => (
        <TextField
          {...params}
          error={error}
          helperText={helperText}
          label={label}
          InputProps={{
            ...params.InputProps,
            type: 'search',
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress size={28} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
          onChange={onChangeInput}
        />
      )}
      ListboxProps={{
        onScroll: (e) => fetchFunction(e, search ?? ''),
        role: 'list-box',
        style: {
          maxHeight: 300,
        },
      }}
      disabled={disabled}
      sx={sx}
    />
  );
};

export default AutoComplete;
